// Fotos im Büro (E3, S3): das Foto mit der Zeichnung als Ebene darüber –
// dieselbe Darstellung wie am iPhone, weil das Bild der Zeichnung aus dem
// Kern kommt (zeichnungSvgInhalt: nur Zahlen, kein Text vom Gerät). Die
// Datei kommt direkt vom Datei-Weg des Büros (/api/crm/datei/<prüfsumme>);
// der Browser schickt die Anmeldung von selbst mit.
//
// Ein Klick auf ein Foto öffnet es groß über der Seite: mit Zeichnung, den
// Notizen zum Foto und ihren Sprachmemos; Pfeile (auch ← und →) zum
// nächsten Foto, Esc oder ein Klick daneben schließt.
//
// Seit S6 stehen in der großen Ansicht auch die Fragen – zum Foto und zu
// jeder seiner Notizen –, mit Antworten, „erledigt“ und „Frage stellen“
// (Grundsatz 9: an dem Ding, um das es geht). Am Laptop (ab 1000 px) rechts
// neben dem Foto, schmaler darunter. Wer in ein Feld tippt, blättert nicht:
// Pfeile bewegen dann den Cursor, Esc schließt nur das Feld.
//
// Seit E3c, S7 (O88): Das große Foto lässt sich zoomen – samt Zeichnung, am
// Laptop mit dem Mausrad, am iPad mit zwei Fingern, dazu „−“ und „+“ oben
// und ein Doppelklick (Doppeltipp); gezoomt verschiebt man es durch Ziehen.
// „Original öffnen“ zeigt die Datei in einem neuen Tab (ohne Zeichnung).
// Die Rechnung steht in zoom.ts.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ZeigerEreignis, type RefObject } from "react";
import { zeichnungSvgInhalt, type CrmAufnahme, type Zeichnung } from "@happy-energy/kern";
import { Ikon } from "@happy-energy/oberflaeche";
import { dateiAdresse, nachtragMarke, wannZeigen } from "../anzeige.ts";
import { FragenAmStueck } from "../fragen/FragenAmStueck.tsx";
import { fragenAn, type FragenJeStueck } from "../fragen/fragen.ts";
import { MemoListe, NotizText } from "./notizen.tsx";
import { doppelt, OHNE_ZOOM, radFaktor, verschieben, ZOOM_HOECHSTENS, ZOOM_SCHRITT, zoomen, zoomProzent, zoomStil, type Feld, type ZoomStand } from "./zoom.ts";

export type FotoAmServer = CrmAufnahme["fotos"][number];

/**
 * Die Zeichnung als Ebene über dem Foto; „decken“ legt sie wie ein Foto mit
 * object-fit: cover (Kachel), „einpassen“ wie object-fit: contain (seit S4:
 * die Unterlage für den Monteur – das ganze Foto in einem Feld fester Höhe,
 * nichts abgeschnitten).
 */
export function ZeichenEbene({
  zeichnung,
  breite,
  hoehe,
  decken,
  einpassen = false,
}: {
  zeichnung: Zeichnung | null;
  breite: number;
  hoehe: number;
  decken: boolean;
  einpassen?: boolean;
}) {
  if (!zeichnung || zeichnung.striche.length === 0 || breite < 1 || hoehe < 1) return null;
  return (
    <svg
      className="zeichen-ebene"
      viewBox={`0 0 ${breite} ${hoehe}`}
      preserveAspectRatio={einpassen ? "xMidYMid meet" : decken ? "xMidYMid slice" : "none"}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: zeichnungSvgInhalt(zeichnung, breite, hoehe) }}
    />
  );
}

/**
 * Ein Foto mit seiner Zeichnung. „kachel“: im Raster, im Feld 4:3; sonst
 * ganz, im Seitenverhältnis des Fotos. Seit E3b (O80) zeigt auch die Kachel
 * das ganze Foto samt Zeichnung – vorher war es auf 4:3 beschnitten, und was
 * am Rand gezeichnet war, fiel weg (Gernot an Manuels Besichtigungen,
 * 22.10.2026). Lädt es nicht (Netz weg, Datei fehlt), steht da mit Worten,
 * warum – nie ein kaputtes Bild.
 */
export function FotoBild({ foto, kachel }: { foto: FotoAmServer; kachel: boolean }) {
  const [kaputt, setKaputt] = useState<string | null>(null);
  const stil = kachel ? undefined : ({ "--seitenverhaeltnis": foto.breite / foto.hoehe } as CSSProperties);
  return (
    <div className={`foto${kachel ? " foto-kachel-ganz" : " foto-gross-bild"}`} style={stil}>
      {kaputt === foto.pruefsumme ? (
        <span className="foto-leer foto-fehlt" role="img" aria-label="Foto lässt sich nicht laden">
          <Ikon name="achtung" />
          {!kachel && <span className="foto-leer-text">Das Foto lässt sich gerade nicht laden – Verbindung prüfen und die Seite neu laden.</span>}
        </span>
      ) : (
        <img src={dateiAdresse(foto.pruefsumme)} alt="" loading={kachel ? "lazy" : "eager"} decoding="async" onError={() => setKaputt(foto.pruefsumme)} />
      )}
      <ZeichenEbene zeichnung={foto.zeichnung} breite={foto.breite} hoehe={foto.hoehe} decken={false} einpassen={kachel} />
    </div>
  );
}

/**
 * Die kleinen Zeichen unten an einer Foto-Kachel (seit E3b, O80): gezeichnet,
 * Notiz, Sprachmemo – so sieht man beim Durchscrollen, wo es mehr gibt.
 */
export function FotoZeichen({ gezeichnet, notiz, memos }: { gezeichnet: boolean; notiz: boolean; memos: number }) {
  if (!gezeichnet && !notiz && memos === 0) return null;
  return (
    <span className="foto-zeichen">
      {gezeichnet && (
        <span className="foto-zeichen-eins" title="gezeichnet">
          <Ikon name="stift" groesse="k" />
        </span>
      )}
      {notiz && (
        <span className="foto-zeichen-eins" title="mit Notiz">
          <Ikon name="notiz" groesse="k" />
        </span>
      )}
      {memos > 0 && (
        <span className="foto-zeichen-eins" title={memos === 1 ? "mit Sprachmemo" : `mit ${memos} Sprachmemos`}>
          <Ikon name="mikro" groesse="k" />
        </span>
      )}
    </span>
  );
}

/** Das Feld des großen Fotos: Größe und Mitte auf dem Bildschirm. */
function feldVon(ref: RefObject<HTMLDivElement | null>): Feld & { mitteX: number; mitteY: number } {
  const r = ref.current?.getBoundingClientRect();
  if (!r) return { breite: 1, hoehe: 1, mitteX: 0, mitteY: 0 };
  return { breite: r.width, hoehe: r.height, mitteX: r.left + r.width / 2, mitteY: r.top + r.height / 2 };
}

type ZoomSetzen = (neu: (alt: ZoomStand) => ZoomStand) => void;

/**
 * Das große Foto zum Zoomen (O88): Foto und Zeichnung in EINER Ebene, die als
 * Ganzes vergrößert und verschoben wird. Mausrad (auch Strg + Rad und das
 * Touchpad) und zwei Finger zoomen um die Stelle darunter; Ziehen verschiebt,
 * sobald gezoomt ist; Doppelklick oder Doppeltipp springt hinein und zurück.
 */
function ZoomFoto({ foto, feld, z, setZ }: { foto: FotoAmServer; feld: RefObject<HTMLDivElement | null>; z: ZoomStand; setZ: ZoomSetzen }) {
  const finger = useRef(new Map<number, { x: number; y: number }>());
  const tipp = useRef<{ zeit: number; x: number; y: number; bewegt: boolean } | null>(null);
  const [zieht, setZieht] = useState(false);

  // Das Mausrad mit eigenem Zuhörer, nicht „passiv“ – sonst zoomte der Browser die ganze Seite.
  useEffect(() => {
    const el = feld.current;
    if (!el) return;
    const rad = (e: WheelEvent) => {
      e.preventDefault();
      const g = feldVon(feld);
      const delta = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY;
      setZ((alt) => zoomen(alt, radFaktor(delta), g, { x: e.clientX - g.mitteX, y: e.clientY - g.mitteY }));
    };
    el.addEventListener("wheel", rad, { passive: false });
    return () => el.removeEventListener("wheel", rad);
  }, [feld, setZ]);

  function runter(e: ZeigerEreignis<HTMLDivElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Ein Zeiger, den der Browser nicht (mehr) kennt – dann eben ohne Festhalten.
    }
    finger.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (finger.current.size === 1) {
      const t = tipp.current;
      // Ein zweiter Tipp kurz danach an fast derselben Stelle: Doppeltipp (das iPad kennt keinen Doppelklick).
      const doppeltipp = t !== null && !t.bewegt && e.timeStamp - t.zeit < 320 && Math.hypot(e.clientX - t.x, e.clientY - t.y) < 30;
      tipp.current = doppeltipp ? null : { zeit: e.timeStamp, x: e.clientX, y: e.clientY, bewegt: false };
      if (doppeltipp) {
        const g = feldVon(feld);
        setZ((alt) => doppelt(alt, g, { x: e.clientX - g.mitteX, y: e.clientY - g.mitteY }));
      }
    } else tipp.current = null;
    setZieht(true);
  }

  function bewegen(e: ZeigerEreignis<HTMLDivElement>) {
    const vorher = finger.current.get(e.pointerId);
    if (!vorher) return;
    const jetzt = { x: e.clientX, y: e.clientY };
    if (tipp.current && Math.hypot(jetzt.x - tipp.current.x, jetzt.y - tipp.current.y) > 6) tipp.current.bewegt = true;
    const g = feldVon(feld);
    const andere = [...finger.current].find(([id]) => id !== e.pointerId)?.[1];
    if (andere) {
      // Zwei Finger: zoomen um die Mitte zwischen ihnen – und mit ihr mitgehen.
      const abstandVorher = Math.hypot(vorher.x - andere.x, vorher.y - andere.y);
      const abstandJetzt = Math.hypot(jetzt.x - andere.x, jetzt.y - andere.y);
      const mitteVorher = { x: (vorher.x + andere.x) / 2 - g.mitteX, y: (vorher.y + andere.y) / 2 - g.mitteY };
      const mitteJetzt = { x: (jetzt.x + andere.x) / 2 - g.mitteX, y: (jetzt.y + andere.y) / 2 - g.mitteY };
      if (abstandVorher > 0) {
        setZ((alt) => verschieben(zoomen(alt, abstandJetzt / abstandVorher, g, mitteVorher), mitteJetzt.x - mitteVorher.x, mitteJetzt.y - mitteVorher.y, g));
      }
    } else {
      setZ((alt) => verschieben(alt, jetzt.x - vorher.x, jetzt.y - vorher.y, g));
    }
    finger.current.set(e.pointerId, jetzt);
  }

  function hoch(e: ZeigerEreignis<HTMLDivElement>) {
    finger.current.delete(e.pointerId);
    if (finger.current.size === 0) setZieht(false);
  }

  const stil = { "--seitenverhaeltnis": foto.breite / foto.hoehe } as CSSProperties;
  return (
    <div
      ref={feld}
      className={`foto-zoom${z.s > 1.001 ? " gezoomt" : ""}${zieht ? " zieht" : ""}`}
      style={stil}
      onPointerDown={runter}
      onPointerMove={bewegen}
      onPointerUp={hoch}
      onPointerCancel={hoch}
      // Ein Bild zieht der Browser sonst als Datei weg.
      onDragStart={(e) => e.preventDefault()}
    >
      <div className="foto-zoom-ebene" style={{ transform: zoomStil(z) }}>
        <FotoBild foto={foto} kachel={false} />
      </div>
    </div>
  );
}

/** „−“, „250 %“, „+“ und „Original öffnen“ – oben in der großen Ansicht. */
function ZoomLeiste({ foto, feld, z, setZ }: { foto: FotoAmServer; feld: RefObject<HTMLDivElement | null>; z: ZoomStand; setZ: ZoomSetzen }) {
  const ganz = z.s <= 1.001;
  return (
    <div className="foto-zoom-leiste" role="group" aria-label="Zoomen">
      <button type="button" className="foto-gross-knopf klein" aria-label="Kleiner (−)" disabled={ganz} onClick={() => setZ((alt) => zoomen(alt, 1 / ZOOM_SCHRITT, feldVon(feld)))}>
        <Ikon name="minus" />
      </button>
      <button type="button" className="foto-zoom-prozent" title="Zurück auf ganze Größe (0)" disabled={ganz} onClick={() => setZ(() => OHNE_ZOOM)}>
        {zoomProzent(z)}
      </button>
      <button
        type="button"
        className="foto-gross-knopf klein"
        aria-label="Größer (+)"
        disabled={z.s >= ZOOM_HOECHSTENS}
        onClick={() => setZ((alt) => zoomen(alt, ZOOM_SCHRITT, feldVon(feld)))}
      >
        <Ikon name="plus" />
      </button>
      <a className="foto-zoom-original" href={dateiAdresse(foto.pruefsumme)} target="_blank" rel="noopener noreferrer" title="Das Foto ohne Zeichnung in einem neuen Tab">
        <Ikon name="pfeil" groesse="k" /> Original öffnen
      </a>
    </div>
  );
}

/** Ein Foto mit dem, was die große Ansicht dazu zeigt. */
export interface FotoImZusammenhang {
  foto: FotoAmServer;
  /** „Station 2 · Weg vom Dach zum Technikraum“ */
  station: string;
  /** Das wievielte an seiner Station, und von wie vielen. */
  nr: number;
  von: number;
}

/** Das Foto groß, über der Seite. */
export function FotoGross({
  a,
  fotos,
  index,
  setIndex,
  schliessen,
  gruppen,
  neuLaden,
}: {
  a: CrmAufnahme;
  fotos: readonly FotoImZusammenhang[];
  index: number;
  setIndex: (i: number) => void;
  schliessen: () => void;
  /** Die Fragen der Besichtigung nach Stück (../fragen/fragen.ts, `nachBezug`). */
  gruppen: FragenJeStueck;
  neuLaden: () => void;
}) {
  const zu = useRef<HTMLButtonElement>(null);
  const feld = useRef<HTMLDivElement>(null);
  // Der Zoom gehört zu einem Foto – beim Blättern steht das nächste wieder ganz da.
  const fotoId = fotos[index]?.foto.id ?? null;
  const [zoom, setZoom] = useState<{ fotoId: string | null; z: ZoomStand }>({ fotoId: null, z: OHNE_ZOOM });
  const z = zoom.fotoId === fotoId ? zoom.z : OHNE_ZOOM;
  const setZ = useCallback<ZoomSetzen>(
    (neu) => setZoom((alt) => ({ fotoId, z: neu(alt.fotoId === fotoId ? alt.z : OHNE_ZOOM) })),
    [fotoId],
  );
  const aktuell = useRef({ index, anzahl: fotos.length, setIndex, schliessen, setZ });
  useEffect(() => {
    aktuell.current = { index, anzahl: fotos.length, setIndex, schliessen, setZ };
  });

  useEffect(() => {
    const vorher = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    zu.current?.focus();
    const taste = (e: KeyboardEvent) => {
      // Im Feld einer Frage oder Antwort gehören die Tasten dem Feld.
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      const { index: i, anzahl, setIndex: setze, schliessen: schliesse, setZ: zoome } = aktuell.current;
      if (e.key === "Escape") schliesse();
      else if (e.key === "ArrowRight" && i < anzahl - 1) setze(i + 1);
      else if (e.key === "ArrowLeft" && i > 0) setze(i - 1);
      // Zoomen mit den Tasten: + und −, 0 zurück auf ganze Größe.
      else if (e.key === "+" || e.key === "=") zoome((alt) => zoomen(alt, ZOOM_SCHRITT, feldVon(feld)));
      else if (e.key === "-") zoome((alt) => zoomen(alt, 1 / ZOOM_SCHRITT, feldVon(feld)));
      else if (e.key === "0") zoome(() => OHNE_ZOOM);
      else return;
      e.preventDefault();
    };
    document.addEventListener("keydown", taste);
    // Die Seite darunter scrollt nicht mit.
    const ueberlauf = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", taste);
      document.body.style.overflow = ueberlauf;
      vorher?.focus();
    };
  }, []);

  const f = fotos[index];
  if (!f) return null;
  const notizen = a.notizen.filter((n) => n.fotoId === f.foto.id);
  const marke = nachtragMarke(f.foto.nachtragId, a);

  return (
    <div
      className="foto-gross"
      role="dialog"
      aria-modal="true"
      aria-label={`Foto ${f.nr} von ${f.von} – ${f.station}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) schliessen();
      }}
    >
      <div className="foto-gross-kopf">
        <div className="foto-gross-titel">
          <b>{f.station}</b>
          <span>
            Foto {f.nr} von {f.von}
            {f.foto.aufnahmezeit && ` · aufgenommen ${wannZeigen(f.foto.aufnahmezeit)}`}
          </span>
        </div>
        {marke && <span className={`sendungs-marke${marke.neu ? "" : " alt"}`}>{marke.text}</span>}
        <ZoomLeiste foto={f.foto} feld={feld} z={z} setZ={setZ} />
        <button type="button" ref={zu} className="foto-gross-knopf" aria-label="Schließen (Esc)" onClick={schliessen}>
          <Ikon name="kreuz" />
        </button>
      </div>
      <div className="foto-gross-mitte">
        <div
          className="foto-gross-buehne"
          onClick={(e) => {
            if (e.target === e.currentTarget) schliessen();
          }}
        >
          <button
            type="button"
            className="foto-gross-knopf foto-gross-pfeil"
            aria-label="Voriges Foto (←)"
            disabled={index === 0}
            onClick={() => setIndex(index - 1)}
          >
            <Ikon name="links" />
          </button>
          <ZoomFoto key={f.foto.id} foto={f.foto} feld={feld} z={z} setZ={setZ} />
          <button
            type="button"
            className="foto-gross-knopf foto-gross-pfeil"
            aria-label="Nächstes Foto (→)"
            disabled={index >= fotos.length - 1}
            onClick={() => setIndex(index + 1)}
          >
            <Ikon name="rechts" />
          </button>
        </div>
        <div className="foto-gross-fuss">
          {notizen.length === 0 ? (
            <span className="foto-gross-leer">Keine Notiz zu diesem Foto.</span>
          ) : (
            notizen.map((n) => (
              <div key={n.id} className="foto-gross-notiz">
                <NotizText a={a} notiz={n} />
                <MemoListe a={a} notizId={n.id} dunkel />
                <FragenAmStueck
                  aufnahmeId={a.id}
                  bezug={{ tabelle: "notiz", id: n.id }}
                  fragen={fragenAn(gruppen, { tabelle: "notiz", id: n.id })}
                  neuLaden={neuLaden}
                  wo="zu dieser Notiz"
                />
              </div>
            ))
          )}
          <section className="foto-gross-fragen" aria-label="Fragen zu diesem Foto">
            <h3>Fragen zu diesem Foto</h3>
            {/* Je Foto ein eigener Baustein: Ein angefangener Text bleibt nicht am nächsten Foto hängen. */}
            <FragenAmStueck
              key={f.foto.id}
              aufnahmeId={a.id}
              bezug={{ tabelle: "foto", id: f.foto.id }}
              fragen={fragenAn(gruppen, { tabelle: "foto", id: f.foto.id })}
              neuLaden={neuLaden}
              wo="zu diesem Foto"
            />
          </section>
          <span className="foto-gross-zaehler">
            {index + 1} / {fotos.length} · ← → blättern · Mausrad oder zwei Finger: zoomen · Esc schließt
          </span>
        </div>
      </div>
    </div>
  );
}
